import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {t} from 'i18next';
import {Box, Grid, Typography} from '@mui/material';
import AurDrawerHeader from '../common/aur-drawer/AurDrawerHeader';
import AurDrawer from '../common/aur-drawer/AurDrawer';
import AurButton from '../common/aur-button/AurButton';
import AurDrawerPanelContainer from '../client-details/AurDrawerPanelContainer';
import AurIcon, {IconColors, IconNames} from '../common/aur-icon/AurIcon';
import AddEditPermissionGroup, {OperationCode} from './AddEditPermissionGroup';
import {
  setPermissionGroups,
  setRefreshPermissionGroups,
  removePermissionGroup,
  setAllPermissionGroups,
} from '../../redux/slices/permissionGroupSlice';
import AurAccordion from '../common/aur-accordion/AurAccordion';
import AurDialog from '../common/aur-dialog/AurDialog';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {
  deletePermissionGroup,
  getPermissionGroupList,
} from '../../api/ClientManagementService';
import AurAskLioDrawerHeaderButton from '../aur-ask-lio/AurAskLioDrawerHeaderButton';
import {Permission} from '../../types/permission-group/permission';
import {resetPermission} from '../../redux/slices/permissionSlice';
import {
  PERMISSION_GROUP_CATEGORY,
  PERMISSION_GROUP_FILTER_TYPE,
  PERMISSION_TYPE,
  CLIENT_PERMISSION_CODES_CONSTANTS,
  MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS,
  BUSINESS_INTELLIGENCE_PERMISSION_CODES_CONSTANTS,
} from '../../constants/constants';
import {PermissionGroup} from '../../types/permission-group/permissionGroup';
import AurMenu from '../common/aur-menu/AurMenu';
import AurSearchBox from '../common/aur-search-box/AurSearchBox';
import {AurDropdownButton} from '../common/aur-button/AurDropdownButton';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import {RootState} from '../../redux/store';
import {
  FeatureSettingEnum,
  isFeatureSettingEnabled,
} from '../../redux/slices/featureSettingsSlice';

type PermissionGroupDrawerProps = {
  shouldShow: boolean;
  onClose: (shouldForceClose?: boolean) => void;
  clientId: string;
  clientName: string;
  onUpdate: (savedPermissionGroup?: PermissionGroup) => void;
  showAddGrpOnOpen: boolean;
  budgetConsumptionEnabled: boolean;
  category?: PERMISSION_GROUP_CATEGORY;
};

const PermissionGroupDrawer = (props: PermissionGroupDrawerProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>();
  const [groupFilter, setGroupFilter] = useState<PERMISSION_GROUP_FILTER_TYPE>(
    PERMISSION_GROUP_FILTER_TYPE.ALL
  );
  const isAddMenuOpen = Boolean(menuAnchorEl);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [selectedPermissionGroup, setSelectedPermissionGroup] = useState<
    string | null
  >(null);
  const [showAddEditGroup, setShowAddEditGroup] = useState(
    props.showAddGrpOnOpen
  );
  const [skipCloseConfirm, setSkipCloseConfirm] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [permissionGroup, setPermissionGroup] = useState<PermissionGroup[]>([]);
  const {permissionGroupDrawer_dontSaveDialog_cofirmBtnId} = useSelector(
    (state: RootState) => state.qualtrics
  );
  const isPowerBIReportsEnabled = useSelector((state: RootState) =>
    isFeatureSettingEnabled(state, FeatureSettingEnum.PowerBIReportsEnabled)
  );
  const {showToast} = useToast();
  const dispatch = useDispatch();

  const ECI_ORDER = [
    'ECI_SEARCHES',
    'ECI_SAVED_SEARCHES',
    'ECI_SEARCH_TERM_REPORT',
    'ECI_DOCUMENT_PREVIEW',
    'ECI_MANAGE_SAVED_SEARCHES',
    'ECI_SAVED_SEARCH_TEMPLATES',
    'ECI_AI_INVESTIGATE',
    'ECI_CREATE_DOC_SUMMARY',
    'ECI_DOCUMENT_TAGGING',
    'ECI_MANAGE_TAG_FIELDS',
    'ECI_MANAGE_CUSTOM_FIELDS',
    'ECI_EXPORTS',
    'ECI_INDEXING',
    'ECI_OVERLAYS',
    'ECI_AUDIT_REPORTS',
  ];

  const [selectedCategory, setSelectedCategory] = useState<number>(
    props.category ?? PERMISSION_GROUP_CATEGORY.CLIENT
  );

  useEffect(() => {
    if (props.category) {
      setSelectedCategory(props.category);
    }
  }, [props.category]);

  const getECICaption = (permissions: Permission[]) => {
    return permissions
      .sort(
        (a, b) =>
          ECI_ORDER.indexOf(a.permissionCode) -
          ECI_ORDER.indexOf(b.permissionCode)
      )
      .map((p) => p.name)
      .join(', ');
  };
  const handleAddMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleAddMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const onAddClientPermissionGroup = () => {
    setSelectedPermissionGroup(null);
    setSelectedCategory(PERMISSION_GROUP_CATEGORY.CLIENT);
    setSkipCloseConfirm(true);
    setShowAddEditGroup(true);
  };

  const onAddProjectPermissionGroup = () => {
    setSelectedPermissionGroup(null);
    setSelectedCategory(PERMISSION_GROUP_CATEGORY.PROJECT);
    setSkipCloseConfirm(true);
    setShowAddEditGroup(true);
  };

  const onEditPermissionGroup = (value: PermissionGroup) => {
    setSelectedPermissionGroup(value.permissionGroupId);
    setSelectedCategory(value.category);
    setSkipCloseConfirm(false);
    setShowAddEditGroup(true);
  };

  const onDeletePermissionGroup = (id: string) => {
    setSelectedPermissionGroup(id);
    setDeleteModalOpen(true);
  };

  const onDelete = () => {
    setDeleteModalOpen(false);
    sendDeletePermissionGroup();
    setSelectedPermissionGroup(null);
  };

  const onDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedPermissionGroup(null);
  };

  const onCloseOk = () => {
    setShowAddEditGroup(false);
    setCloseModal(false);
    dispatch(resetPermission());
    props.onClose(true);
  };

  const onCloseCancel = () => {
    setCloseModal(false);
  };

  const onCancelOk = () => {
    setShowAddEditGroup(false);
    setCancelModal(false);
    dispatch(resetPermission());
  };

  const onCancel = () => {
    setCancelModal(false);
  };
  const handleDrawerClose = () => {
    if (skipCloseConfirm) {
      setSkipCloseConfirm(false);
      props.onClose(true);
    } else {
      setCloseModal(true);
    }
  };

  const sendDeletePermissionGroup = async () => {
    try {
      const result = await deletePermissionGroup(
        props.clientId,
        selectedPermissionGroup || '' // Default to an empty string if null
      );

      if (result.data.meta.status === 'Success') {
        showToast(result.data.meta.message, 'success');

        dispatch(removePermissionGroup(selectedPermissionGroup));
        //Not required to fetch entire permission groups which is a time consuming api call, causing a noticable delay
        //fetchPermissionGroups(); // Refresh the permission group list
        setSelectedPermissionGroup(null);
        setSkipCloseConfirm(true);
        setPermissionGroup((prev) =>
          prev.filter(
            (group) => group.permissionGroupId !== selectedPermissionGroup
          )
        );
      } else if (result.data.meta.status === 'Failed') {
        showToast(result.data.meta.message, 'error');
      }
    } catch (e) {
      console.error(e);
      showToast(t('toast.errorDeletingPermissionGroup'), 'error');
    } finally {
      props.onUpdate();
    }
  };

  const fetchPermissionGroups = async (
    category?: PERMISSION_GROUP_CATEGORY,
    search?: string
  ) => {
    if (props.clientId === '' || !props.shouldShow) return;
    try {
      //Commented this line to fix the blank screen issue as per ARHD-17315
      //dispatch(resetPermissionGroups());

      // Get permission groups
      setShowLoader(true);
      const result = await getPermissionGroupList(
        props.clientId,
        category,
        search
      );
      const permissionGroupData = result.data.data;

      //Permission group users are now fetched with the getPermissionGroupList call, so the seperate calls to
      //getPermissionGroupUsers not required

      // const permissionGroups = permissionGroupData.permissionGroups;
      // Get permission group users
      // Append a "users" prop to the original model
      // for (const permissionGroup of permissionGroups) {
      //  const userResults = await getPermissionGroupUsers(
      //    props.clientId,
      //    permissionGroup.permissionGroupId
      //  );
      //  const users = userResults.data.data.permissionGroupMembers;
      //  permissionGroup.users = users;
      // }
      setPermissionGroup(permissionGroupData?.permissionGroups ?? []);
      dispatch(setPermissionGroups(permissionGroupData));
      if (!category && !search) {
        dispatch(setAllPermissionGroups(permissionGroupData));
      }
      setShowLoader(false);
    } catch (e) {
      console.error(e);
    } finally {
      setShowLoader(false);
      setIsInitialLoadComplete(true);
    }
  };

  const formatItems = useMemo(() => {
    const hasSearchText = (searchKeyword ?? '').trim().length > 0;
    // Empty or Zero state handling
    if (isInitialLoadComplete && !showLoader && !permissionGroup?.length) {
      if (hasSearchText) {
        return (
          <Box className="permission-group-empty-state search-empty-state">
            <Box className="permission-group-empty-content">
              <Typography className="permission-group-empty-title typo-text subhead2_size">
                {t('permissionGroupList.noResultsFoundTitle')}
              </Typography>

              <Typography className="permission-group-empty-description typo-text body2_size">
                {t('permissionGroupList.noResultsFoundDescription')}
              </Typography>
            </Box>
          </Box>
        );
      }

      return (
        <Box className="permission-group-empty-state">
          <Box className="permission-group-empty-content">
            <Typography className="permission-group-empty-title typo-text subhead2_size">
              {t('permissionGroupList.noPermissionGroupsTitle')}
            </Typography>

            <Typography className="permission-group-empty-description typo-text body2_size">
              {t('permissionGroupList.noPermissionGroupsDescription')}
            </Typography>
          </Box>

          <AurButton
            classNames="permission-group-empty-button"
            type="continue"
            label={t('permissionGroupList.addPermissionGroup')}
            onClick={handleAddMenuClick}
            startIcon={
              <AurIcon
                icon={IconNames.Add}
                color={IconColors.Custom}
                customClassName="icon-white"
              />
            }
          />
        </Box>
      );
    }

    return permissionGroup?.map((value) => {
      let reportPermissions = '';
      let clientSettingsPermissions = '';
      let projectSettingsPermissions = '';
      let evidenceUploadCaption = '';
      let businessIntelligencePermissions = '';

      const eciPermissions = value.permissions.filter(
        (item) => item.type === PERMISSION_TYPE.ECI
      );

      const eciCaption =
        eciPermissions.length > 0 ? getECICaption(eciPermissions) : '';
      if (value.permissions) {
        const reportPermissionCodesToExclude = new Set<string>([
          CLIENT_PERMISSION_CODES_CONSTANTS.CLIENTS_VIEW,
          CLIENT_PERMISSION_CODES_CONSTANTS.USERS_VIEW,
          CLIENT_PERMISSION_CODES_CONSTANTS.MANAGE_PERMISSION_GROUPS,
          MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.MANAGE_PROJECT_SETTINGS,
          MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.EDIT_PROJECT_PROFILE,
          MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.CREATE_NEW_PROJECT,
          BUSINESS_INTELLIGENCE_PERMISSION_CODES_CONSTANTS.PROJECT_LEVEL_REPORTS,
        ]);
        const reports = value.permissions.filter(
          (item) =>
            item.type !== PERMISSION_TYPE.ECI &&
            item.type !== PERMISSION_TYPE.EVIDENCE &&
            !reportPermissionCodesToExclude.has(item.permissionCode)
        );
        const businessIntelligence = value.permissions.filter((item) =>
          [
            BUSINESS_INTELLIGENCE_PERMISSION_CODES_CONSTANTS.PROJECT_LEVEL_REPORTS,
          ].includes(item.permissionCode as any)
        );
        const clientSettings = value.permissions.filter((item) =>
          [
            CLIENT_PERMISSION_CODES_CONSTANTS.CLIENTS_VIEW,
            CLIENT_PERMISSION_CODES_CONSTANTS.USERS_VIEW,
            CLIENT_PERMISSION_CODES_CONSTANTS.MANAGE_PERMISSION_GROUPS,
          ].includes(item.permissionCode as any)
        );

        const projectSettings = value.permissions.filter((item) =>
          [
            MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.MANAGE_PROJECT_SETTINGS,
            MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.EDIT_PROJECT_PROFILE,
            MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.CREATE_NEW_PROJECT,
          ].includes(item.permissionCode as any)
        );
        const evidenceUploadPermission = value.permissions.filter(
          (item) => item.type === PERMISSION_TYPE.EVIDENCE
        );
        reportPermissions = reports.map((p) => p.name).join(', ');
        clientSettingsPermissions = clientSettings
          .map((p) => p.name)
          .join(', ');
        projectSettingsPermissions = projectSettings
          .map((p) => p.name)
          .join(', ');
        evidenceUploadCaption = evidenceUploadPermission
          .map((value): string => value?.name ?? '')
          .join(', ');
        businessIntelligencePermissions = businessIntelligence
          .map((p) => p.name)
          .join(', ');
      }

      const titleContent = (
        <>
          <Grid item xs="auto">
            <AurButton
              onClick={() => onDeletePermissionGroup(value.permissionGroupId)}
              type="secondary"
              classNames="permission-group-title-buttons"
              ariaLabel={t('permissionGroupList.removeBranch', {
                branchName: value.name,
              })}
            >
              <AurIcon icon={IconNames.Remove} />
            </AurButton>
          </Grid>
          <Grid item xs="auto">
            <AurButton
              onClick={() => onEditPermissionGroup(value)}
              type="secondary"
              classNames="permission-group-title-buttons"
              ariaLabel={t('permissionGroupList.editBranch', {
                branchName: value.name,
              })}
            >
              <AurIcon icon={IconNames.Edit} />
            </AurButton>
          </Grid>
        </>
      );
      return (
        <AurAccordion
          collapsed={true}
          alertWarningIcon={false}
          title={
            <Box className="permission-group-title-wrapper">
              <Box
                className={`permission-group-pill ${
                  value.category === PERMISSION_GROUP_CATEGORY.CLIENT
                    ? 'client-pill'
                    : 'project-pill'
                }`}
              >
                {value.category === PERMISSION_GROUP_CATEGORY.CLIENT
                  ? t('conciergeReports.formLabels.client')
                  : t('conciergeReports.formLabels.project')}
              </Box>

              <Typography className="permission-group-name typo-text subhead2_size">
                {value.name}
              </Typography>
            </Box>
          }
          titleContent={titleContent}
          key={value.permissionGroupId}
        >
          <>
            <div className="permission-group-expanded-section">
              <div className="system-defined-message-container">
                <Typography className="typo-text body2_size detail-label">
                  {value.description}
                </Typography>
              </div>
              <div className="allowed-permission-container">
                <Typography className="typo-text subhead3_size permissions-heading">
                  {t('permissionGroupList.allowedPermission')}
                </Typography>
                <div className="permission-box">
                  {/* Client Settings and Manage Users */}
                  {value.category === PERMISSION_GROUP_CATEGORY.CLIENT && (
                    <div>
                      <Typography className="typo-text label3_size title-label">
                        {t('permissionGroupList.system')}
                      </Typography>
                      <Typography className="typo-text body2_size detail-label">
                        {clientSettingsPermissions || '---'}
                      </Typography>
                    </div>
                  )}
                  {/* Project Settings */}
                  <div>
                    <Typography className="typo-text label3_size title-label">
                      {value.category === PERMISSION_GROUP_CATEGORY.CLIENT
                        ? t('permissionGroupList.projectAdministration')
                        : t('permissionGroupList.manageProject')}
                    </Typography>
                    <Typography className="typo-text body2_size detail-label">
                      {projectSettingsPermissions || '---'}
                    </Typography>
                  </div>
                  {/** Reports Permission */}
                  <div>
                    <Typography className="typo-text label3_size title-label">
                      {t('permissionGroupList.reporting')}
                    </Typography>
                    <Typography className="typo-text body2_size detail-label">
                      {reportPermissions || '---'}
                    </Typography>
                  </div>
                  {isPowerBIReportsEnabled && (
                    <div>
                      <Typography className="typo-text label3_size title-label">
                        {t('permissionGroupList.businessIntelligence')}
                      </Typography>
                      <Typography className="typo-text body2_size detail-label">
                        {businessIntelligencePermissions || '---'}
                      </Typography>
                    </div>
                  )}
                  {value.category === PERMISSION_GROUP_CATEGORY.PROJECT && (
                    <>
                      {/** ECA Functionality Permission */}
                      <div>
                        <Typography className="typo-text label3_size title-label">
                          {t('permissionGroupList.ecaFunctionality')}
                        </Typography>
                        <Typography className="typo-text body2_size detail-label">
                          {eciCaption || '---'}
                        </Typography>
                      </div>
                      {/** Evidence Source Management Permission */}
                      <div>
                        <Typography className="typo-text label3_size title-label">
                          {t('permissionGroupList.evidenceSourceManagement')}
                        </Typography>
                        <Typography className="typo-text body2_size detail-label">
                          {evidenceUploadCaption || '---'}
                        </Typography>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        </AurAccordion>
      );
    });
  }, [permissionGroup, searchKeyword, showLoader, isInitialLoadComplete, isPowerBIReportsEnabled]);
  const getCategoryValue = (
    filter: PERMISSION_GROUP_FILTER_TYPE
  ): number | undefined => {
    switch (filter) {
      case PERMISSION_GROUP_FILTER_TYPE.CLIENT:
        return PERMISSION_GROUP_CATEGORY.CLIENT;

      case PERMISSION_GROUP_FILTER_TYPE.PROJECT:
        return PERMISSION_GROUP_CATEGORY.PROJECT;

      default:
        return undefined;
    }
  };

  useEffect(() => {
    const categoryValue = getCategoryValue(groupFilter);
    fetchPermissionGroups(categoryValue, searchKeyword);
  }, [props.clientId, props.shouldShow, groupFilter, searchKeyword]);

  useEffect(() => {
    setShowAddEditGroup(props.showAddGrpOnOpen);
    dispatch(setRefreshPermissionGroups(false));
  }, [props.showAddGrpOnOpen, props.shouldShow]);

  const upsertPermissionGroupInList = (savedGroup: PermissionGroup) => {
    setPermissionGroup((prev) => {
      const existing = prev.find(
        (group) => group.permissionGroupId === savedGroup.permissionGroupId
      );
      const groupToSave: PermissionGroup = {
        ...savedGroup,
        users:
          savedGroup.users?.length > 0
            ? savedGroup.users
            : (existing?.users ?? []),
      };
      const merged = (
        existing
          ? prev.map((group) =>
              group.permissionGroupId === savedGroup.permissionGroupId
                ? groupToSave
                : group
            )
          : [...prev, groupToSave]
      ).sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      return merged;
    });
  };

  const onAddNewPermissionGroupSuccess = (savedGroup?: PermissionGroup) => {
    if (savedGroup) {
      upsertPermissionGroupInList(savedGroup);
    } else {
      fetchPermissionGroups();
    }
    setShowAddEditGroup(false);
    props.onUpdate();
  };

  const panelHeader = (
    <Box className="permission-group-header-wrapper">
      <Box className="permission-group-drawer-header">
        <AurButton
          classNames="drawer-close-button"
          label={t('label.close')}
          type="exit"
          size="Medium"
          title={t('label.close')}
          onClick={handleDrawerClose}
          dataTestId="permission-group-close"
        />
        <AurAskLioDrawerHeaderButton />
      </Box>
      <Box className="permission-group-header-separator" />
    </Box>
  );
  const filterOptions = [
    {
      id: PERMISSION_GROUP_FILTER_TYPE.ALL,
      label: t('common.all'),
    },
    {
      id: PERMISSION_GROUP_FILTER_TYPE.CLIENT,
      label: t('userHomePage.clientPermGroup'),
    },
    {
      id: PERMISSION_GROUP_FILTER_TYPE.PROJECT,
      label: t('userHomePage.projectPermGroup'),
    },
  ];

  const selectedFilterOption =
    filterOptions.find((option) => option.id === groupFilter) ||
    filterOptions[0];

  const dropdownButtonText =
    selectedFilterOption.id === PERMISSION_GROUP_FILTER_TYPE.ALL
      ? `View: ${selectedFilterOption.label}`
      : selectedFilterOption.label;

  const showEmptyState =
    isInitialLoadComplete &&
    !showLoader &&
    !permissionGroup?.length &&
    !(searchKeyword ?? '').trim().length;

  const panelContainer = (
    <AurDrawerPanelContainer
      panelHeader={panelHeader}
      contentHeader={
        <>
          <div className="permission-group-drawer-header-section">
            <AurDrawerHeader
              title={t('permissionGroupList.managePermission')}
              titleCssClass="typo-text headline1_size drawer-header-label"
              buttons={[
                <AurButton
                  key={0}
                  classNames="permission-group-header-add-button"
                  type="continue"
                  title={t('buttons.add')}
                  startIcon={
                    <AurIcon
                      icon={IconNames.Add}
                      color={IconColors.Custom}
                      customClassName="permission-group-header-add-icon"
                    />
                  }
                  label={t('buttons.add')}
                  size="Large"
                  onClick={handleAddMenuClick}
                />,
              ]}
            />
          </div>
          <AurMenu
            anchorElement={menuAnchorEl}
            shouldShow={isAddMenuOpen}
            onClose={handleAddMenuClose}
            menuItems={[
              {
                label: t('userHomePage.clientPermGroup'),
                onClick: onAddClientPermissionGroup,
              },
              {
                label: t('userHomePage.projectPermGroup'),
                onClick: onAddProjectPermissionGroup,
              },
            ]}
            direction="right"
          />
        </>
      }
      contentBody={
        <Box>
          {!showEmptyState && (
            <Box className="permission-group-filter-row">
              <Box className="permission-group-search-box">
                <AurSearchBox
                  placeHolder={t('common.search')}
                  variant="outlined"
                  label=""
                  value={searchKeyword ?? ''}
                  shouldSearchOnMount={false}
                  onSearch={(keyword: string) => {
                    setShowLoader(true);
                    setSearchKeyword(keyword.trim() || undefined);
                  }}
                />
              </Box>
              <Box className="permission-group-dropdown">
                <AurDropdownButton
                  elementID="permission-group-filter-dropdown"
                  buttonText={dropdownButtonText}
                  type="secondary"
                  aria-live="polite"
                  size="Medium"
                  iconType="Arrow"
                  iconSize="medium"
                  menuDirectionLeft={false}
                  menuItemHeight="medium"
                  menuItemWidth="medium"
                  menuItemCustomWidth="260px"
                  menuItemTextSize="small"
                  children={
                    <AurIcon
                      icon={IconNames.CaretDown}
                      color={IconColors.Custom}
                      customClassName="permission-group-dropdown-icon"
                    />
                  }
                  menuItems={filterOptions.map((option) => ({
                    label: option.label,
                    onClick: () => {
                      setGroupFilter(option.id);
                    },
                  }))}
                />
              </Box>
            </Box>
          )}
          <Box className="permission-group-list-container">{formatItems}</Box>
        </Box>
      }
    />
  );

  return (
    <>
      <AurDrawer
        className="client-drawer"
        open={props.shouldShow}
        onClose={handleDrawerClose}
        anchor="right"
        drawerContent={
          showAddEditGroup ? (
            <AddEditPermissionGroup
              onClose={() => {
                props.onClose();
              }}
              category={selectedCategory}
              clientId={props.clientId}
              budgetConsumptionEnabled={
                props?.budgetConsumptionEnabled ?? false
              }
              onAddSuccess={(savedPermissionGroup) => {
                if (props.showAddGrpOnOpen) {
                  props.onUpdate(savedPermissionGroup);
                  props.onClose();
                } else {
                  onAddNewPermissionGroupSuccess(savedPermissionGroup);
                  setSkipCloseConfirm(true);
                }
              }}
              onCancel={() => {
                if (props.showAddGrpOnOpen) {
                  setCloseModal(true);
                } else {
                  setCancelModal(true);
                }
              }}
              operationCode={
                selectedPermissionGroup ? OperationCode.edit : OperationCode.add
              }
              permissionGroupId={selectedPermissionGroup || ''}
            />
          ) : (
            panelContainer
          )
        }
      />
      <AurDialog
        className="delete-permission-dialog"
        popupContent={
          <div className="email-dialog info-modify">
            <Typography className="typo-text subhead4_size">
              {t('permissionGroupList.deleteConfirmText')}
            </Typography>
          </div>
        }
        popupTitle={t('permissionGroupList.deleteConfirmMsg')}
        labelActionOk={t('buttons.delete')}
        labelActionCancel={t('buttons.cancel')}
        onOk={onDelete}
        onCancel={onDeleteCancel}
        showDialog={deleteModalOpen}
        buttonStyle="sideBySide"
        okLabelClassName="delete-confirm-button"
      />

      <AurDialog
        popupTitle={t('dialog.areYouSure')}
        popupContent={t('dialog.cancelPopupContent')}
        labelActionOk={t('dialog.yesCancel')}
        labelActionCancel={t('dialog.stayOnPanel')}
        cancelButtonTitle={t('dialog.stayOnPanel')}
        onOk={onCancelOk}
        onCancel={onCancel}
        showDialog={cancelModal}
        buttonStyle="sideBySide"
        confirmBtnId={permissionGroupDrawer_dontSaveDialog_cofirmBtnId}
      />

      <AurDialog
        popupTitle={t('dialog.areYouSure')}
        popupContent={t('dialog.closePopupContent')}
        labelActionOk={t('dialog.yesClose')}
        labelActionCancel={t('dialog.stayOnPanel')}
        cancelButtonTitle={t('dialog.stayOnPanel')}
        onOk={onCloseOk}
        onCancel={onCloseCancel}
        showDialog={closeModal}
        buttonStyle="sideBySide"
      />
      <AurLoadingIndicator overlay={true} isOpen={showLoader} />
    </>
  );
};

export default PermissionGroupDrawer;
